// Shared utility functions for consistent token rendering across components

export interface TokenDisplayData {
  key: string;
  amount: number;
  currency: string;
  status: string;
  expiry: string;
  message?: string;
  isInvoice?: boolean;
  hasPrepaidCard?: boolean;
  prepaidCard?: any;
}

export interface TokenDisplayOptions {
  showActions: boolean;
  showQRCode: boolean;
  theme: 'default' | 'minimal' | 'compact' | 'premium';
  className: string;
}

/**
 * Determines if a transaction represents a token that should be rendered as TokenCard
 */
export const isTokenTransaction = (transaction: any): boolean => {
  // Handle both backend API format (key, tokenType) and platform API format (tokenKey)
  const tokenKey = transaction.key || transaction.tokenKey;
  
  if (!tokenKey) {
    return false;
  }
  
  // Check if it's a 6-character token key (standard token format)
  if (tokenKey.length !== 6) {
    return false;
  }
  
  // If we have tokenType, exclude system transactions
  if (transaction.tokenType) {
    return !['topup', 'referral', 'balance_up'].includes(transaction.tokenType);
  }
  
  // For platform API transactions, detect token transactions by:
  // 1. Having a tokenKey or key that's 6 characters
  // 2. Not being a system transaction (deposit, withdraw)
  // 3. Being a send/receive/debit/credit type (token operations)
  const tokenTransactionTypes = ['send', 'receive', 'debit', 'credit', 'redeem'];
  return tokenTransactionTypes.includes(transaction.type);
};



/**
 * Determines if a token is in an active state (should show actions like copy/QR)
 */
export const isActiveToken = (status: string, isRecipient: boolean = false): boolean => {
  const activeStatuses = ['PENDING', 'UNFUNDED', 'REVEALED', 'TRANSFERRED', 'PARTIAL', 'ACTIVE'];
  return activeStatuses.includes(status.toUpperCase()) && !isRecipient;
};

/**
 * Converts transaction data to TokenCard format
 */
export const createTokenDisplayData = (transaction: any): TokenDisplayData => {
  // Use actual expiry date from transaction data - do not fake it for existing tokens
  const actualExpiry = transaction.expires || transaction.expiry || transaction.expiresAt || (transaction.data && transaction.data.validity && transaction.data.validity.end) || transaction.valid || transaction.validUntil;
  
  // Debug expiry detection
  console.log('🔧 createTokenDisplayData expiry detection for', transaction.key, ':', {
    expires: transaction.expires,
    expiry: transaction.expiry,
    expiresAt: transaction.expiresAt,
    dataValidityEnd: transaction.data?.validity?.end,
    valid: transaction.valid,
    validUntil: transaction.validUntil,
    finalActualExpiry: actualExpiry
  });
  
  return {
    key: transaction.key || transaction.tokenKey,
    amount: parseFloat(transaction.amount) || 0,
    currency: transaction.currency || 'USD',
    status: transaction.status, // Use backend status exactly as provided
    expiry: actualExpiry || '', // Use actual expiry or empty string if not available
    message: transaction.message || (
      transaction.description && transaction.description.includes(' - ') 
        ? transaction.description.split(' - ')[1] 
        : undefined
    ),
    isInvoice: transaction.isInvoice || transaction.tokenType === 'invoice',
    // Include prepaid card data from transformed transaction
    hasPrepaidCard: transaction.hasPrepaidCard,
    prepaidCard: transaction.prepaidCard
  };
};

/**
 * Determines display options for a token based on its state
 */
export const getTokenDisplayOptions = (transaction: any): TokenDisplayOptions => {
  const showActions = isActiveToken(transaction.status, transaction.recipient);
  
  return {
    showActions,
    showQRCode: true,
    theme: 'default',
    className: showActions ? "border-blue-200" : "border-gray-200"
  };
};

/**
 * Gets appropriate status text for token display
 */
export const getTokenStatusText = (transaction: any): string => {
  if (transaction.recipient) {
    return `Redeemed`;
  } else if (transaction.status?.toLowerCase() === 'completed') {
    return `Completed`;
  } else {
    return `Created`;
  }
}; 